import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { DateTime } from 'luxon';

import { SavedTemplate, TemplateStats } from '@/types/saved-template';
import type { RotaTemplateScope, ShiftSet } from '@/types/template';

import { scheduleToDays } from './day-of-week';

/**
 * Calculate total shifts per week for a template
 * Each shift set creates individual shifts = staff count × days
 */
export function calculateTotalShifts(shifts: ShiftSet[]): number {
  return shifts.reduce(
    (total, shift) => total + resolveSlotsPerDay(shift) * scheduleToDays(shift.schedule).length,
    0,
  );
}

/**
 * Calculate total hours per week for a template
 * Total hours = shift duration × staff count × days
 */
export function calculateTotalHours(shifts: ShiftSet[]): number {
  return shifts.reduce((total, shift) => {
    const shiftHours = calculateShiftDuration(shift.startTime, shift.endTime);
    return total + shiftHours * resolveSlotsPerDay(shift) * scheduleToDays(shift.schedule).length;
  }, 0);
}

/**
 * Calculate shifts by assignment type
 * Counts individual shifts (staff count × days) for each type
 */
export function calculateShiftsByAssignmentType(shifts: ShiftSet[]): {
  autoAssigned: number;
  specificEmployee: number;
  open: number;
} {
  let autoAssigned = 0;
  let specificEmployee = 0;
  let open = 0;

  shifts.forEach((shift) => {
    const individualShifts = resolveSlotsPerDay(shift) * scheduleToDays(shift.schedule).length;

    if (shift.staffing.method === 'automatic') {
      autoAssigned += individualShifts;
    } else if (shift.staffing.method === 'specific') {
      specificEmployee += individualShifts;
    } else if (shift.staffing.method === 'open') {
      open += individualShifts;
    }
  });

  return { autoAssigned, specificEmployee, open };
}

/**
 * Get template statistics
 */
export function getTemplateStats(template: SavedTemplate): TemplateStats {
  return {
    totalShifts: calculateTotalShifts(template.shifts),
    totalHoursPerWeek: calculateTotalHours(template.shifts),
  };
}

/**
 * Format relative date (e.g., "2 days ago"). Falls back to a translated
 * "Unknown" when the input string is unparseable or Luxon returns `null`
 * (which it does for invalid `DateTime` instances). Luxon's relative
 * formatter follows the active Lingui locale.
 */
export function formatRelativeDate(dateString: string): string {
  const dt = DateTime.fromISO(dateString).setLocale(i18n.locale);
  if (!dt.isValid) return i18n._(msg`Unknown`);
  return dt.toRelative() ?? i18n._(msg`Unknown`);
}

/**
 * Format a template's site scope for display. The contract `RotaTemplateScope`
 * carries `sites` as `Identifiable[]` (names included), so no external lookup is
 * needed; an empty/undefined `sites` means organisation-wide.
 */
export function formatSiteNames(scope: RotaTemplateScope): { display: string; tooltip?: string } {
  const siteNames = (scope.sites ?? []).map((site) => site.name);

  if (siteNames.length === 0) {
    return { display: i18n._(msg`Organisation-wide`) };
  }

  if (siteNames.length === 1) {
    return { display: siteNames[0] };
  }

  if (siteNames.length === 2) {
    return { display: siteNames.join(', ') };
  }

  // 3+ sites: show first 2 and "+ X more"
  const first = siteNames[0];
  const second = siteNames[1];
  const extra = siteNames.length - 2;
  const display = i18n._(msg`${first}, ${second} + ${extra} more`);
  const tooltip = siteNames.join(', ');

  return { display, tooltip };
}

/**
 * Format last used date.
 *
 * Bucket labels (`Used today` / `Used X days ago` / ...) deliberately
 * use explicit if/else rather than ICU `plural()` so each catalogue
 * entry is a plain sentence Azure Translator can handle cleanly.
 * Luxon supplies the day diff (via `diffNow`) and the locale-aware
 * date for the tooltip; the conditional structure stays in our code.
 */
export function formatLastUsed(lastUsedAt?: string): { display: string; tooltip?: string } {
  if (!lastUsedAt) {
    return { display: i18n._(msg`Never used`) };
  }

  const dt = DateTime.fromISO(lastUsedAt).setLocale(i18n.locale);
  if (!dt.isValid) return { display: i18n._(msg`Never used`) };

  const diffInDays = Math.floor(-dt.diffNow('days').days);

  let display: string;
  if (diffInDays === 0) {
    display = i18n._(msg`Used today`);
  } else if (diffInDays === 1) {
    display = i18n._(msg`Used yesterday`);
  } else if (diffInDays < 14) {
    display = i18n._(msg`Used ${diffInDays} days ago`);
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    display = weeks === 1 ? i18n._(msg`Used 1 week ago`) : i18n._(msg`Used ${weeks} weeks ago`);
  } else if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30);
    display = months === 1 ? i18n._(msg`Used 1 month ago`) : i18n._(msg`Used ${months} months ago`);
  } else {
    const years = Math.floor(diffInDays / 365);
    display = years === 1 ? i18n._(msg`Used 1 year ago`) : i18n._(msg`Used ${years} years ago`);
  }

  const tooltip = i18n._(msg`Last used: ${dt.toLocaleString(DateTime.DATE_MED)}`);
  return { display, tooltip };
}

/**
 * Number of shift slots a single shift contributes per day for template totals.
 * `specific` and `open` always count as one slot per day; `automatic` derives
 * its count from `staffing.count`. Distinct from `resolveInstanceCount`
 * (staffing.ts), which counts staff pills to render and treats `open` by its
 * configured quantity.
 */
function resolveSlotsPerDay(shift: ShiftSet): number {
  if (shift.staffing.method === 'specific' || shift.staffing.method === 'open') {
    return 1;
  }
  return shift.staffing.count;
}

/**
 * Calculate duration of a single shift in hours. Handles overnight
 * shifts (e.g. 22:00 → 06:00) by wrapping the difference past midnight.
 */
export function calculateShiftDuration(startTime: string, endTime: string): number {
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);

  let duration = endHour * 60 + endMinute - (startHour * 60 + startMinute);

  // Handle overnight shifts
  if (duration < 0) {
    duration += 24 * 60;
  }

  return duration / 60; // Convert to hours
}
